import { z } from "zod";
import { PaginationSchema } from "@/components/paginate/types";

// Dashboard Insight Schema
export const DashboardInsightSchema = z.object({
  name: z.string(),
  description: z.string(),
});

export type DashboardInsight = z.infer<typeof DashboardInsightSchema>;

export const BIOpportunitySchema = z.object({
  opportunity_id: z.string(),
  opportunity_name: z.string(),
  description: z.string(),
  money_savings: z
    .object({
      min: z.number(),
      max: z.number(),
    })
    .optional(),
  time_savings: z.string().optional(),
  effort: z.number().optional(),
  status: z.number().optional(),
  last_seen: z.string().optional(),
  first_seen: z.string().optional(),
  count: z.number().optional(),
  total_count: z.number().optional(),
});

export type BIOpportunity = z.infer<typeof BIOpportunitySchema>;

// BiReport Schema (Zod version)
export const BiReportSchema = z.object({
  rk: z.string(),
  dashboard: z.string(),
  workbook: z.string(),
  owner: z.string(),
  site_name: z.string(),
  refreshFrequency: z.string(),
  insights: z.array(DashboardInsightSchema),
  views: z.number(),
  refreshes: z.number(),
  usage_drift: z.string().optional(),
  time_impact: z.string().optional(),
  tableauTags: z.array(z.string()),
  project_name: z.string().optional(),
  worksheet_count: z.number().optional(),
  user_count: z.number().optional(),
  last_viewed: z.string().nullish(),
  last_updated: z.string().optional(),
  views_heatmap: z.array(z.array(z.number())).nullish(),
  refresh_heatmap: z.array(z.array(z.number())).nullish(),
  instance_name: z.string().optional(),
  llm_insights: z.string().nullish(),
  total_cost: z.number().nullable().optional(),
  instrumentation_snippet: z.string().nullish(),
  dashboard_url: z.string().optional(),
  opportunities: z.array(BIOpportunitySchema).optional(),
  has_lineage: z.boolean(),
  views_30d: z.number().optional(),
  refreshes_30d: z.number().optional(),
  cost_attribution_type: z.string().nullish(),
});

export type BiReport = z.infer<typeof BiReportSchema>;

// Paginated BiReport response schema
export const BiReportsPaginatedResponseSchema = z.object({
  ...PaginationSchema.shape,
  items: z.array(BiReportSchema),
});

export type BiReportsPaginatedResponse = z.infer<typeof BiReportsPaginatedResponseSchema>;

// Dashboard Summary Schema
export const BiDashboardSummaryItemSchema = z.object({
  label: z.string(),
  value: z.union([z.number(), z.string()]),
  info: z.string().optional(),
});

export type BiDashboardSummaryItem = z.infer<typeof BiDashboardSummaryItemSchema>;

export const BiDashboardSummarySchema = z.array(BiDashboardSummaryItemSchema);

export type BiDashboardSummary = z.infer<typeof BiDashboardSummarySchema>;

export interface HeatMapDataType {
  z: number[][];
  x: string[];
  y: string[];
}

// BI Report Filters Schema
export const BiReportFiltersSchema = z.record(z.array(z.string()));

export type BiReportFilters = z.infer<typeof BiReportFiltersSchema>;

// Dashboard Activity Schemas
export const DashboardAccessActivitySchema = z.object({
  date: z.string(), // YYYY-MM-DD format
  count: z.number(), // Number of accesses on this date
});

export type DashboardAccessActivity = z.infer<typeof DashboardAccessActivitySchema>;

export const DashboardRefreshActivitySchema = z.object({
  date: z.string(), // YYYY-MM-DD format
  count: z.number(), // Number of refreshes on this date
});

export type DashboardRefreshActivity = z.infer<typeof DashboardRefreshActivitySchema>;

// Paginated response schemas using the established pattern
export const DashboardAccessActivityResponseSchema = z.object({
  ...PaginationSchema.shape,
  items: z.array(DashboardAccessActivitySchema),
});

export type DashboardAccessActivityResponse = z.infer<typeof DashboardAccessActivityResponseSchema>;

export const DashboardRefreshActivityResponseSchema = z.object({
  ...PaginationSchema.shape,
  items: z.array(DashboardRefreshActivitySchema),
});

export const DashboardDownloadFiltersSchema = z.object({
  sort_by: z.string().optional(),
  sort: z.string().optional(),
  dashboard: z.array(z.string()).optional(),
  workbook: z.array(z.string()).optional(),
  owner: z.array(z.string()).optional(),
  tableau_tags: z.array(z.string()).optional(),
  insights: z.array(z.string()).optional(),
  site_name: z.array(z.string()).optional(),
  instance_name: z.array(z.string()).optional(),
  opportunity_name: z.array(z.string()).optional(),
  start_date: z.string().optional(),
  end_date: z.string().optional(),
});

// Query params schema for paginated dashboards API
export const BiDashboardsQueryParamsSchema = DashboardDownloadFiltersSchema.extend({
  page: z.number().optional(),
  page_size: z.number().optional(),
});

export type BiDashboardsQueryParams = z.infer<typeof BiDashboardsQueryParamsSchema>;

export type DashboardRefreshActivityResponse = z.infer<
  typeof DashboardRefreshActivityResponseSchema
>;

// User Report Schemas
export const UserInsightSchema = z.object({
  name: z.string(),
  reasons: z.array(z.string()),
});

export type UserInsight = z.infer<typeof UserInsightSchema>;

export const BiUserReportSchema = z.object({
  email: z.string(),
  views: z.number(),
  dashboards_created: z.number(),
  workbooks_created: z.number(),
  insights: z.array(UserInsightSchema), // Only support UserInsightSchema
  last_activity: z.string().nullish(),
  full_name: z.string().optional(),
  views_received: z.number(),
  current_license_type: z.string().nullish(),
  instance_name: z.string().nullish(),
  user_rk: z.string().nullish(),
  opportunities: z.array(BIOpportunitySchema),
});

export type BiUserReport = z.infer<typeof BiUserReportSchema>;

export const BiUserSummarySchema = z.object({
  label: z.string(),
  value: z.number(),
  info: z.string().optional(),
});

export type BiUserSummary = z.infer<typeof BiUserSummarySchema>;

// Unified filter schema for both regular filters and download/CSV filters
export const BiUserReportsFiltersSchema = z.object({
  sort_by: z.string().optional(),
  sort: z.string().optional(),
  views_period: z.number().optional(),
  user: z.array(z.string()).optional(),
  license_type: z.array(z.string()).optional(),
  insights: z.array(z.string()).optional(),
  instance_name: z.array(z.string()).optional(),
  opportunity_name: z.array(z.string()).optional(),
});

export type BiUserReportsFilters = z.infer<typeof BiUserReportsFiltersSchema>;

// Alias for backwards compatibility and clarity
export const BiUserReportsDownloadFiltersSchema = BiUserReportsFiltersSchema;
export type BiUserReportsDownloadFilters = BiUserReportsFilters;

// Union type for all user report filters
export type BiUserReportsAllFilters = BiUserReportsFilters | BiUserReportsDownloadFilters;

// Query params for getUserReports API with pagination and date range
export type GetUserReportsParams = {
  page: number;
  page_size: number;
  sort_by: string;
  sort: string;
  start_date?: string;
  end_date?: string;
  user?: string | string[];
  license_type?: string | string[];
  insights?: string | string[];
  instance_name?: string | string[];
  opportunity_name?: string | string[];
};
